// Copyright The Linux Foundation and each contributor to LFX.
// SPDX-License-Identifier: MIT
import { Component, OnInit, OnChanges, OnDestroy } from '@angular/core';
import { FormBuilder, FormGroup, Validators, FormControl } from '@angular/forms';
import { Observable, Subscription, BehaviorSubject } from 'rxjs';
import { map, take, switchMap } from 'rxjs/operators';
import { Store, select } from '@ngrx/store';
import { PolicyFormDetails, PolicyService, GoAction, QueueAlertAction, AlertType, PolicyEffectType } from '@app/core';
import {
  State,
  selectPolicyStatementSelections,
  StatementSelections,
  AddStatementAction,
  ClearPolicyFormAction,
  SetPolicyDetailsAction,
  selectPolicyFormDetails,
  selectPolicyDraft,
  LoadingPolicyAction,
  SelectEffectAction,
  AddStatementWithEffectAction
} from '../../state';
import { INPUT_TEXT_REQUIRED_ERROR,
  NAME_PATTERN_ERROR, DESCRIPTION_PATTERN_ERROR, NAME_PATTERN, DESCRIPTION_PATTERN } from '@app/core/services/input-text-validator.service';

@Component({
  selector: 'app-policy-create-new',
  templateUrl: './policy-create-new.component.html',
  styleUrls: ['./policy-create-new.component.scss']
})
export class PolicyCreateNewComponent implements OnInit, OnChanges, OnDestroy {

  addStatementMode = true;
  reviewPolicyMode = false;

  nameControlErrors = [
    INPUT_TEXT_REQUIRED_ERROR,
    NAME_PATTERN_ERROR
  ];

  descriptionControlErrors = [
    INPUT_TEXT_REQUIRED_ERROR,
    DESCRIPTION_PATTERN_ERROR,
  ];

  readonly columns = [
    { name: 'Effect', prop: 'effect' },
    { name: 'Service', prop: 'serviceName' },
    { name: 'Resource', prop: 'resourceName' },
    { name: 'Actions', prop: 'actionNames' }
  ];

  policyForm: FormGroup;
  detailsSubscription: Subscription | undefined;
  policyNameControl: FormControl = new FormControl();
  policyDescriptionControl: FormControl = new FormControl();
  readonly statementSelections$: Observable<StatementSelections[]>;
  readonly isSubmitting$ = new BehaviorSubject<boolean>(false);
  readonly failedSubmit$ = new BehaviorSubject<boolean>(false);
  private submitSubscription: Subscription | undefined = undefined;
  constructor(
    private store: Store<State>,
    private policyService: PolicyService,
    private formBuilder: FormBuilder) {
    this.policyForm = this.buildPolicyForm();
    this.policyNameControl = this.policyForm.get('policyName') as FormControl;
    this.policyDescriptionControl = this.policyForm.get('policyDescription') as FormControl;
    this.statementSelections$ = this.store.pipe(select(selectPolicyStatementSelections));
    this.store.dispatch(new ClearPolicyFormAction());
  }

  ngOnInit() {
    this.hydrateForm();
    this.addStatement();
  }

  ngOnChanges() {
    this.hydrateForm();
  }

  ngOnDestroy() {
    if (this.detailsSubscription) {
      this.detailsSubscription.unsubscribe();
    }
    if (this.submitSubscription) {
      this.submitSubscription.unsubscribe();
    }
  }

  cancel() {
    this.policyForm.reset();
    this.store.dispatch(new ClearPolicyFormAction());
  }

  makeChanges () {
    this.addStatementMode = true;
    this.reviewPolicyMode = false;
    this.policyNameControl.enable();
    this.policyDescriptionControl.enable();
  }

  review() {
    this.policyNameControl.disable();
    this.policyDescriptionControl.disable();

    const { policyName: name, policyDescription: description } = this.policyForm.value;
    this.store.dispatch(
      new SetPolicyDetailsAction({
        name,
        description
      })
    );
    this.addStatementMode = false;
    this.reviewPolicyMode = true;
  }

  addStatement() {
    this.store.dispatch(new AddStatementWithEffectAction());
  }

  trackByStatementIdx(idx: number) {
    return `statement${idx}`;
  }

  submit() {
    this.setSubmitting();
    const submit$ = this.store.pipe(
      select(selectPolicyDraft),
      switchMap(policy => this.policyService.create(policy))
    );
    submit$.subscribe(
      _ => {
        this.store.dispatch(new ClearPolicyFormAction());
        this.store.dispatch(new GoAction(['/iam/policies']));
        this.store.dispatch(new LoadingPolicyAction());
        this.store.dispatch(
          new QueueAlertAction({
            alertText: `Policy has successfully been created.`,
            alertType: AlertType.SUCCESS
          })
        );
      },
      () => {
        this.failSubmitting();
        this.store.dispatch(new QueueAlertAction({ alertText: `Policy delete failed.`, alertType: AlertType.ERROR }));
      }
    );
  }

  private setSubmitting() {
    this.isSubmitting$.next(true);
    this.failedSubmit$.next(false);
  }

  private failSubmitting() {
    this.isSubmitting$.next(false);
    this.failedSubmit$.next(true);
  }

  get statementsInvalid$() {
    const isInvalid = (statementSelections: StatementSelections) =>
      statementSelections.effect === undefined ||
      statementSelections.service === undefined ||
      statementSelections.resource === undefined ||
      statementSelections.actions === undefined;
    return this.statementSelections$.pipe(map(selections => selections.some(isInvalid)));
  }

  private buildPolicyForm() {
    return this.formBuilder.group({
      policyName: ['', [
          Validators.required,
          Validators.pattern(NAME_PATTERN)
        ]],
      policyDescription: ['', [
          Validators.required,
          Validators.pattern(DESCRIPTION_PATTERN)
        ]]
    });
  }

  private hydrateForm() {
    this.detailsSubscription = this.store
      .pipe(
        select(selectPolicyFormDetails),
        take(1)
      )
      .subscribe(policyDetails => this.setFormValues(policyDetails));
  }

  private setFormValues(policyDetails: PolicyFormDetails) {
    this.policyForm.setValue({
      policyName: policyDetails.name,
      policyDescription: policyDetails.description
    });
  }
}



// WEBPACK FOOTER //
// ./src/app/access-management/policies/policy-create-new/policy-create-new.component.ts